import React, { useEffect } from "react";

import type {
  ItineraryStop as ItineraryStopType,
  NormalizedTripStopAndTransfers
} from "@app/components/itinerary/types";
import { OUTBOUND_LEG_INDEX, RETURN_LEG_INDEX } from "@app/constants/trip";
import { useAppContext } from "@app/helpers/hooks";
import { TripStopsFetchStatusKey } from "@app/modules/results/state/trip-stops-fetch-status";
import { showedUdmItinerary } from "@app/tracking/itinerary-tracking";

import type { StateProps } from "./itinerary.connect";

import { ArrivalStop } from "./arrival-stop";
import { DepartureStop } from "./departure-stop";
import {
  isTransferStop,
  isDepartureStop,
  isArrivalStop,
  isIntermediateStopList,
  isItineraryStopWithDepartureTime,
  isItineraryStopWithArrivalTime
} from "./guards";
import { IntermediateStops } from "./intermediate-stops";
import { TransferStop } from "./transfer";

export type OwnProps = {
  renderLoader?: () => React.ReactElement;
  trip_id?: string | undefined;
  direction?: "outbound" | "return";
};

export type Props = StateProps & OwnProps;

export const Itinerary: React.FC<Props> = ({
  direction,
  hide_time,
  renderLoader,
  segments,
  trip_id,
  trip_info,
  trip_stops_and_transfers,
  trip_stops_fetch_status
}) => {
  const { tracker } = useAppContext();

  useEffect(() => {
    if (trip_info) {
      const current_trip_leg =
        direction === "outbound" ? OUTBOUND_LEG_INDEX : RETURN_LEG_INDEX;
      const event = showedUdmItinerary({
        trip: trip_info,
        // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        trip_leg: trip_info.trip_legs[current_trip_leg],
        segments
      });
      if (event) {
        tracker.track(event);
      }
    }
  }, [trip_id]);

  if (trip_stops_fetch_status === TripStopsFetchStatusKey.Loading) {
    return renderLoader ? renderLoader() : null;
  }

  const itinerary_item_shared_props = {
    trip_stops_and_transfers,
    direction,
    hide_time
  };

  return (
    <>
      {trip_stops_and_transfers.map((stop, index) => (
        <ItineraryItem
          key={index}
          stop={stop}
          index={index}
          {...itinerary_item_shared_props}
        />
      ))}
    </>
  );
};

type ItineraryItemProps = {
  trip_stops_and_transfers: NormalizedTripStopAndTransfers;
  stop: ItineraryStopType | ItineraryStopType[];
  direction: "outbound" | "return";
  index: number;
  hide_time: boolean | undefined;
};

const ItineraryItem: React.FC<ItineraryItemProps> = ({
  trip_stops_and_transfers,
  stop,
  direction,
  index,
  hide_time
}) => {
  if (isDepartureStop(stop)) {
    return <DepartureStop key="departure" stop={stop} />;
  }

  if (isArrivalStop(stop)) {
    return (
      <ArrivalStop
        key="arrival"
        leg_departure_time={getLegDepartureTime(trip_stops_and_transfers)}
        stop={stop}
      />
    );
  }

  if (isTransferStop(stop)) {
    return (
      <TransferStop
        key={`transfer-${index}`}
        hide_time={hide_time}
        leg_departure_time={getLegDepartureTime(trip_stops_and_transfers)}
        operator={stop.operator}
        stop={stop}
      />
    );
  }

  const departure_stop = trip_stops_and_transfers[index - 1];
  const arrival_stop = trip_stops_and_transfers[index + 1];

  if (
    Array.isArray(stop) &&
    isIntermediateStopList(stop) &&
    // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    isItineraryStopWithDepartureTime(departure_stop) &&
    // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    isItineraryStopWithArrivalTime(arrival_stop)
  ) {
    return (
      <IntermediateStops
        key={`stops-${index}`}
        stops={stop}
        departure_stop={departure_stop}
        arrival_stop={arrival_stop}
        direction={direction}
      />
    );
  }

  return <></>;
};

function getLegDepartureTime(
  trip_stops_and_transfers: NormalizedTripStopAndTransfers
): string | null {
  // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const { departure_time } = isDepartureStop(trip_stops_and_transfers[0])
    ? trip_stops_and_transfers[0]
    : { departure_time: null };

  return departure_time;
}
